import { DatesHelper } from 'utils/datesHelper';
import { EnumsHelper } from 'utils/enumsHelper';
import { makeAutoObservable, makeObservable, observable, toJS, reaction, computed } from 'mobx';
import { format } from 'date-fns';
import { EditableArray } from 'store/cores/EditableArray';
import { DEFAULTS } from 'config/defaultValues';
import { DATE_FORMATS } from 'CONSTANTS';
import { dateParser } from 'models/Boards/tilesFactory';
import uuid from 'uuid-random';

const getDateForCoordinate = (originalDate) => {
  const date = new Date(originalDate);
  date.setUTCHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 19) + 'Z';
};

export class CoordinatesModel {
  constructor({ name, coordinates, groupId, startDate, endDate }) {
    this.id = uuid();
    this.name = name || '';
    this.groupId = groupId || 1;
    // startDate всегда = FootfallModel.startDate
    this.startDate = startDate || new Date();
    this.endDate = endDate || new Date();
    this.coordinates = coordinates || [];
    makeObservable(this, {
      coordinates: observable,
      name: observable,
      endDate: observable,
      isValid: computed,
    });
  }

  get isValid() {
    return this.name.trim().length > 0 && this.coordinates.length > 0;
  }

  changeCoordinates(newCoordinates) {
    this.coordinates = newCoordinates || [];
  }
  correctType({ startDate }) {
    this.startDate = DatesHelper.toServerModel(startDate);
    this.endDate = DatesHelper.toServerModel(this.endDate);
  }

  changeName(name) {
    this.name = name || '';
  }
}

const [MAIN_STAT, GROUP_MAIN_STATS, GROUP_CONTROL_STATS, GROUP_TARGET_STATS] = [
  'MAIN_STAT',
  'GROUP_MAIN_STATS',
  'GROUP_CONTROL_STATS',
  'GROUP_TARGET_STATS',
];

const fields = {
  [MAIN_STAT]: {
    uniqueUsers: 'campaignUniqueUsers',
    uniqueVisits: null,
    dailyUniqueVisits: null,
    visitsCapThree: null,
    adImpressions: 'campaignAdImpressions',
    clicks: 'campaignClicks',
    responsiveRate: null,
    incrementalVisits: 'campaignIncrementalVisits',
    iCR: 'campaignICR',
    cTR: 'campaignCTR',
    upliftUsers: 'campaignUpliftUsers',
  },
  [GROUP_MAIN_STATS]: {
    uniqueUsers: 'uniqueUsers',
    uniqueVisits: 'uniqueVisits',
    dailyUniqueVisits: 'dailyUniqueVisits',
    visitsCapThree: 'visitsCapThree',
    adImpressions: null,
    clicks: null,
    responsiveRate: null,
    incrementalVisits: null,
    iCR: null,
    cTR: null,
    upliftUsers: null,
  },
  [GROUP_CONTROL_STATS]: {
    uniqueUsers: 'uniqueUsersControlGroup',
    uniqueVisits: 'uniqueVisitsControlGroup',
    dailyUniqueVisits: 'dailyUniqueVisitsControlGroup',
    visitsCapThree: 'visitsCapThreeControlGroup',
    adImpressions: null,
    clicks: null,
    responsiveRate: 'responsiveRateControlGroup',
    incrementalVisits: null,
    iCR: null,
    cTR: null,
    upliftUsers: null,
  },
  [GROUP_TARGET_STATS]: {
    uniqueUsers: 'uniqueUsersTargetGroup',
    uniqueVisits: 'uniqueVisitsTargetGroup',
    dailyUniqueVisits: 'dailyUniqueVisitsTargetGroup',
    visitsCapThree: 'visitsCapThreeTargetGroup',
    adImpressions: 'adImpressionsTargetGroup',
    clicks: 'clicksTargetGroup',
    responsiveRate: 'responsiveRateTargetGroup',
    incrementalVisits: 'incrementalVisitsTargetGroup',
    iCR: 'iCRTargetGroup',
    cTR: 'cTRTargetGroup',
    upliftUsers: 'upliftUsersTargetGroup',
  },
};

const changeName = ({ name, type }) => {
  let resultName = name;
  switch (type) {
    case GROUP_CONTROL_STATS:
      resultName += ' Control Group';
      break;
    case GROUP_TARGET_STATS:
      resultName += ' Target Group';
      break;
    default:
      break;
  }

  return resultName;
};

class TableModel {
  constructor({ type, data }) {
    const useFields = fields[type] || {};
    this.name = changeName({ name: data?.name || '', type });
    this.uniqueUsers = data[useFields.uniqueUsers] ?? null;
    this.uniqueVisits = data[useFields.uniqueVisits] ?? null;
    this.dailyUniqueVisits = data[useFields.dailyUniqueVisits] ?? null;
    this.visitsCapThree = data[useFields.visitsCapThree] ?? null;
    this.adImpressions = data[useFields.adImpressions] ?? null;
    this.clicks = data[useFields.clicks] ?? null;
    this.responsiveRate = data[useFields.responsiveRate] ?? null;
    this.incrementalVisits = data[useFields.incrementalVisits] ?? null;
    this.iCR = data[useFields.iCR] ?? null;
    this.cTR = data[useFields.cTR] ?? null;
    this.upliftUsers = data[useFields.upliftUsers] ?? null;
  }
}

export class FootfallModel {
  id = null;
  name = `Footfall ${format(new Date(), DATE_FORMATS.format4)}`;
  agencyId = null;
  startDate = dateParser(DatesHelper.Last7Days(), true);
  endDate = dateParser(DatesHelper.Today(), true);
  offlineEndDate = DatesHelper.toServerModel(DatesHelper.Today());
  periodDate = DEFAULTS.dates.periodDate;
  itemType = 5;
  status = 0;
  campaigns = [];
  folders = [];
  clientCoordinates = new CoordinatesModel({ name: 'Client group', startDate: this.startDate, endDate: this.endDate });
  groupOfCoordinates = [new CoordinatesModel({ startDate: this.startDate, endDate: this.endDate })];
  isShowStatisticTiles = false;
  isByDay = false;
  isTimeToVisit = false;
  isShowControlGroupTiles = false;
  tiles = [];
  returnOnlySummary = null;
  timeZoneId = 0;
  timeZoneOffset = 0;
  startTimeToVisit = dateParser(DatesHelper.Last7Days(), true);
  endTimeToVisit = dateParser(DatesHelper.Today(true));
  attributionPeriod = 7;
  constructor({
    id,
    name,
    agencyId,
    startDate,
    endDate,
    offlineEndDate,
    periodDate,
    itemType,
    status,
    campaigns,
    folders,
    clientCoordinates,
    groupOfCoordinates,
    isShowStatisticTiles,
    isByDay,
    isTimeToVisit,
    isShowControlGroupTiles,
    tiles,
    returnOnlySummary,
    timeZoneOffset,
    timeZoneId,
    startTimeToVisit,
    endTimeToVisit,
    footfallStatistic,
    attributionPeriod,
  }) {
    makeAutoObservable(this, null, {
      autoBind: true,
    });
    reaction(
      // при изменении startDate в модели CoordinatesModel тоже должны меняться startDate
      () => this.startDate,
      (startDate) => {
        this.clientCoordinates.startDate = getDateForCoordinate(startDate);
        this.groupOfCoordinates.forEach((group) => {
          group.startDate = getDateForCoordinate(startDate);
        });
      },
    );

    this.id = id || this.id;
    this.name = name || this.name;
    this.agencyId = agencyId || this.agencyId;
    this.startDate = startDate ? DatesHelper.toServerModel(startDate) : this.startDate;
    this.endDate = endDate ? DatesHelper.toServerModel(endDate) : this.endDate;
    this.offlineEndDate = offlineEndDate ? DatesHelper.toServerModel(offlineEndDate) : this.offlineEndDate;
    this.periodDate = periodDate || this.periodDate;
    this.itemType = itemType || this.itemType;
    this.status = status || this.status;
    this.campaigns = campaigns || this.campaigns;
    this.folders = folders || this.folders;
    this.clientCoordinates = clientCoordinates ? new CoordinatesModel(clientCoordinates) : this.clientCoordinates;

    this.groupOfCoordinates = groupOfCoordinates?.length
      ? groupOfCoordinates.map((group) => new CoordinatesModel(group))
      : this.groupOfCoordinates;
    this.isShowStatisticTiles = isShowStatisticTiles || this.isShowStatisticTiles;
    this.isByDay = isByDay || this.isByDay;
    this.isTimeToVisit = isTimeToVisit || this.isTimeToVisit;
    this.isShowControlGroupTiles = isShowControlGroupTiles || this.isShowControlGroupTiles;
    this.tiles = tiles || this.tiles;
    this.returnOnlySummary = returnOnlySummary || this.returnOnlySummary;
    this.timeZoneOffset = timeZoneOffset || this.timeZoneOffset;
    this.timeZoneId = timeZoneId || this.timeZoneId;
    this.startTimeToVisit = startTimeToVisit ? startTimeToVisit : this.startTimeToVisit;
    this.endTimeToVisit = endTimeToVisit ? endTimeToVisit : this.endTimeToVisit;
    this.footfallStatistic = footfallStatistic || {};
    this.attributionPeriod = attributionPeriod || this.attributionPeriod;
  }

  getCoordinate() {
    const coordinates = [];
    if (this.clientCoordinates.coordinates?.length) {
      coordinates.push(...this.clientCoordinates.coordinates);
    }
    this.groupOfCoordinates.forEach((group) => {
      if (group.coordinates?.length) {
        coordinates.push(...group.coordinates);
      }
    });

    return coordinates;
  }

  getPlainModel() {
    const obj = {};
    this.clientCoordinates.endDate = this.offlineEndDate;

    this.clientCoordinates.correctType({ startDate: this.startDate });
    this.groupOfCoordinates.forEach((item) => item.correctType({ startDate: this.startDate }));
    const keyDate = ['startTimeToVisit', 'endTimeToVisit'];
    const keyDateInitTime = ['startDate', 'endDate'];
    for (let key in this) {
      if (['tiles', 'footfallStatistic'].includes(key)) {
        continue;
      }

      if (this[key] instanceof EditableArray) {
        obj[key] = toJS(this[key].getValue);
      } else {
        const isDateWithTZ = keyDate.includes(key) && typeof this[key] !== 'string';
        const isDateWithOut = keyDateInitTime.includes(key) && typeof this[key] !== 'string';
        obj[key] = toJS(
          isDateWithTZ
            ? DatesHelper.toServerModelWithUTC0(this[key])
            : isDateWithOut
            ? DatesHelper.toServerModel(this[key])
            : this[key],
        );
      }
    }
    if (!obj.isTimeToVisit) {
      obj.startTimeToVisit = null;
      obj.endTimeToVisit = null;
    }

    return obj;
  }

  getMainStatistic() {
    if (this.tiles.length === 0) {
      return [];
    }

    const statistic = [
      new TableModel({
        type: MAIN_STAT,
        data: { ...this.footfallStatistic, name: 'Campaign statistics' },
      }),
      new TableModel({
        type: GROUP_MAIN_STATS,
        data: { ...this.footfallStatistic.clientGroupOfCoordinates, name: 'Client group' },
      }),
    ];

    const statisticControlAndTarget = [
      new TableModel({
        type: GROUP_TARGET_STATS,
        data: { ...this.footfallStatistic.clientGroupOfCoordinates, name: 'Client' },
      }),
      new TableModel({
        type: GROUP_CONTROL_STATS,
        data: { ...this.footfallStatistic.clientGroupOfCoordinates, name: 'Client' },
      }),
    ];

    if (this.footfallStatistic.groupOfCoordinates?.length) {
      this.footfallStatistic.groupOfCoordinates.forEach((group) => {
        const nameGroup =
          this.groupOfCoordinates.find((groupWithInfo) => groupWithInfo.groupId === group.groupId)?.name || '';
        const data = {
          ...group,
          name: nameGroup,
        };
        statistic.push(
          new TableModel({
            type: GROUP_MAIN_STATS,
            data,
          }),
        );

        statisticControlAndTarget.push(
          new TableModel({
            type: GROUP_TARGET_STATS,
            data,
          }),
        );

        statisticControlAndTarget.push(
          new TableModel({
            type: GROUP_CONTROL_STATS,
            data,
          }),
        );
      });
    }

    statistic.push(...statisticControlAndTarget);

    return statistic;
  }
}
